/**
 * SecureEmbed User Preferences Page
 *
 * Persistence model (LIVE DATA ONLY — no no-ops):
 *  - theme is a per-user server-side setting, persisted via settingsApi
 *    (Cognito custom:theme) so it follows the user across devices.
 *  - timezone / dateFormat / defaultTimeRange / notifications are client UI
 *    preferences persisted to localStorage (the backend does not store these),
 *    so they genuinely survive reloads instead of being silently dropped.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Moon, Globe, Clock, Save, Loader2, AlertCircle } from 'lucide-react';
import { settingsApi, UserSettings } from '../services/api';

const LOCAL_PREFS_KEY = 'secureembed.dashboard.preferences';

// Must match the backend's VALID_THEMES (lambda/dashboard/settings/handler.py)
// so an updateSettings call is never rejected with a 400.
const THEME_OPTIONS: { value: NonNullable<UserSettings['theme']>; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'purple', label: 'Purple' },
  { value: 'contrast', label: 'High Contrast' },
  { value: 'green', label: 'Green' },
];

interface LocalPrefs {
  timezone: string;
  dateFormat: string;
  defaultTimeRange: string;
  notifications: {
    email: boolean;
    browser: boolean;
    anomalyAlerts: boolean;
    weeklyReports: boolean;
  };
}

const DEFAULT_LOCAL: LocalPrefs = {
  timezone: 'Australia/Sydney',
  dateFormat: 'DD/MM/YYYY',
  defaultTimeRange: '7d',
  notifications: {
    email: true,
    browser: true,
    anomalyAlerts: true,
    weeklyReports: false,
  },
};

const applyTheme = (theme: string) => {
  // Conventional theming hook — set the attribute themed CSS keys off of.
  document.documentElement.setAttribute('data-theme', theme);
};

const PreferencesPage: React.FC = () => {
  const [theme, setTheme] = useState<NonNullable<UserSettings['theme']>>('light');
  const [local, setLocal] = useState<LocalPrefs>(DEFAULT_LOCAL);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Client-side UI preferences from localStorage
    try {
      const raw = localStorage.getItem(LOCAL_PREFS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setLocal({
          ...DEFAULT_LOCAL,
          ...parsed,
          notifications: { ...DEFAULT_LOCAL.notifications, ...(parsed.notifications || {}) },
        });
      }
    } catch {
      // Malformed localStorage — fall back to defaults, non-fatal.
    }

    // Theme is persisted server-side per user.
    try {
      const resp = await settingsApi.getSettings();
      const serverTheme = resp?.data?.theme;
      if (serverTheme) {
        setTheme(serverTheme);
        applyTheme(serverTheme);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load saved theme');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);

    // Persist client-side UI preferences (non-fatal if storage is unavailable).
    let localOk = true;
    try {
      localStorage.setItem(LOCAL_PREFS_KEY, JSON.stringify(local));
    } catch {
      localOk = false;
    }

    // Persist theme server-side (the authoritative, cross-device setting).
    try {
      await settingsApi.updateSettings({ theme });
      applyTheme(theme);
      if (!localOk) {
        setError('Theme saved, but local UI preferences could not be stored in this browser.');
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Preferences</h2>
        <p className="text-gray-500 mt-1">Customize your dashboard experience.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Appearance */}
      <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-3 mb-4">
          <Moon className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900">Appearance</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as NonNullable<UserSettings['theme']>)}
              className="w-full max-w-xs px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {THEME_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Localization */}
      <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-3 mb-4">
          <Globe className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900">Localization</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
            <select
              value={local.timezone}
              onChange={(e) => setLocal({ ...local, timezone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
              <option value="Australia/Melbourne">Australia/Melbourne (AEST/AEDT)</option>
              <option value="Australia/Brisbane">Australia/Brisbane (AEST)</option>
              <option value="Australia/Adelaide">Australia/Adelaide (ACST/ACDT)</option>
              <option value="Australia/Perth">Australia/Perth (AWST)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
            <select
              value={local.dateFormat}
              onChange={(e) => setLocal({ ...local, dateFormat: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
        </div>
      </div>

      {/* Dashboard Defaults */}
      <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-3 mb-4">
          <Clock className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900">Dashboard Defaults</h3>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Time Range</label>
          <select
            value={local.defaultTimeRange}
            onChange={(e) => setLocal({ ...local, defaultTimeRange: e.target.value })}
            className="w-full max-w-xs px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="1h">Last 1 hour</option>
            <option value="6h">Last 6 hours</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-gray-100 rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-3 mb-4">
          <Bell className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900">Notifications</h3>
        </div>

        <div className="space-y-3">
          {[
            { key: 'email', label: 'Email notifications' },
            { key: 'browser', label: 'Browser notifications' },
            { key: 'anomalyAlerts', label: 'Anomaly alerts' },
            { key: 'weeklyReports', label: 'Weekly summary reports' },
          ].map((item) => (
            <label key={item.key} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={local.notifications[item.key as keyof LocalPrefs['notifications']]}
                onChange={(e) =>
                  setLocal({
                    ...local,
                    notifications: {
                      ...local.notifications,
                      [item.key]: e.target.checked,
                    },
                  })
                }
                className="w-4 h-4 rounded border-gray-300 text-[#0369A1] focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">{item.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-[#0369A1] text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>{saving ? 'Saving...' : 'Save Preferences'}</span>
        </button>
        {saved && (
          <span className="text-sm text-green-600">Preferences saved successfully!</span>
        )}
      </div>
    </div>
  );
};

export default PreferencesPage;
